/**
 * V60.1: useTransferRequestMutations Hook (CF 기반, approvalRequests 단일 SSoT)
 *
 * 전배 요청의 생성/승인/반려를 Cloud Functions를 통해 처리합니다.
 *
 * **Workflow (approvalRequests 단일):**
 * 1. pending → 받는 조직장이 createTransferRequest CF로 요청 생성
 * 2. orgApproved → 보내는 조직장이 approveApprovalRequest CF로 승인
 * 3. applied / approved_scheduled → HR이 hrApproveApprovalRequest CF로 승인
 *
 * **반려:**
 * - pending → rejected (보내는 조직장 반려)
 * - orgApproved → rejected (HR 반려)
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

/** 전배 요청 생성 입력 */
interface CreateTransferRequestInput {
  employeeId: string;
  fromOrgUnitId: string;
  toOrgUnitId: string;
  reason: string;
  transferMode: 'immediate' | 'scheduled';
  effectiveDate?: string;
}

/**
 * useTransferRequestMutations Hook 반환 타입
 */
export interface UseTransferRequestMutationsResult {
  /** 전배 요청 생성 (CF: createTransferRequest) */
  createRequest: (input: CreateTransferRequestInput) => Promise<{ approvalRequestId: string }>;

  /** 보내는 조직장 승인 (CF: approveApprovalRequest) */
  approveByFromOrg: (approvalRequestId: string) => Promise<void>;

  /** HR 승인 (CF: hrApproveApprovalRequest) */
  hrConfirm: (approvalRequestId: string, comment?: string) => Promise<void>;

  /** 보내는 조직장 반려 (CF: rejectApprovalRequest) */
  reject: (approvalRequestId: string, comment?: string) => Promise<void>;

  /** HR 반려 (CF: hrApproveApprovalRequest action='reject') */
  hrReject: (approvalRequestId: string, comment?: string) => Promise<void>;

  /** 제출 중 여부 */
  isSubmitting: boolean;

  /** 에러 */
  error: string | null;
}

/**
 * 전배 요청 Mutations Hook (V60: CF 기반)
 */
export function useTransferRequestMutations(): UseTransferRequestMutationsResult {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 전배 요청 생성 (CF: createTransferRequest)
   */
  const createRequest = useCallback(async (input: CreateTransferRequestInput) => {
    if (!functions) throw new Error('Firebase Functions가 초기화되지 않았습니다.');

    setIsSubmitting(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'createTransferRequest');
      const result = await fn({
        employeeId: input.employeeId,
        fromOrgUnitId: input.fromOrgUnitId,
        toOrgUnitId: input.toOrgUnitId,
        reason: input.reason,
        transferMode: input.transferMode,
        effectiveDate: input.effectiveDate || null,
      });
      const data = result.data as any;
      return {
        approvalRequestId: data.approvalRequestId,
      };
    } catch (err: any) {
      const msg = err?.message || '전배 요청 생성 실패';
      setError(msg);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  /**
   * 보내는 조직장 승인 (CF: approveApprovalRequest)
   */
  const approveByFromOrg = useCallback(async (approvalRequestId: string) => {
    if (!functions) throw new Error('Firebase Functions가 초기화되지 않았습니다.');

    setIsSubmitting(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'approveApprovalRequest');
      await fn({ approvalRequestId });
    } catch (err: any) {
      const msg = err?.message || '승인 처리 실패';
      setError(msg);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  /**
   * HR 승인 (CF: hrApproveApprovalRequest)
   */
  const hrConfirm = useCallback(async (approvalRequestId: string, comment?: string) => {
    if (!functions) throw new Error('Firebase Functions가 초기화되지 않았습니다.');

    setIsSubmitting(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'hrApproveApprovalRequest');
      await fn({ approvalRequestId, action: 'approve', comment: comment || '' });
    } catch (err: any) {
      const msg = err?.message || '인사팀 승인 처리 실패';
      setError(msg);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  /**
   * 보내는 조직장 반려 (CF: rejectApprovalRequest)
   */
  const reject = useCallback(async (approvalRequestId: string, comment?: string) => {
    if (!functions) throw new Error('Firebase Functions가 초기화되지 않았습니다.');

    setIsSubmitting(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'rejectApprovalRequest');
      await fn({ approvalRequestId, comment: comment || '' });
    } catch (err: any) {
      const msg = err?.message || '반려 처리 실패';
      setError(msg);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  /**
   * HR 반려 (CF: hrApproveApprovalRequest action='reject')
   */
  const hrReject = useCallback(async (approvalRequestId: string, comment?: string) => {
    if (!functions) throw new Error('Firebase Functions가 초기화되지 않았습니다.');

    setIsSubmitting(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'hrApproveApprovalRequest');
      await fn({ approvalRequestId, action: 'reject', comment: comment || '반려' });
    } catch (err: any) {
      const msg = err?.message || '인사팀 반려 처리 실패';
      setError(msg);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return {
    createRequest,
    approveByFromOrg,
    hrConfirm,
    reject,
    hrReject,
    isSubmitting,
    error,
  };
}
